import React, { FC, useEffect, useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";
import { Helmet } from "react-helmet-async";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { Link, useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import api from '../../api';
import { useAppSelector, useAppDispatch } from "../../redux/hook";
import { updateFormData, selectFormData } from "../../redux/reducers/formReducer";
import { updateUser } from "../../redux/reducers/userReducer";

export interface PageSignUpProps {
  className?: string;
}

const PageSignUp: FC<PageSignUpProps> = ({ className = "" }) => {
  const formData = useAppSelector(selectFormData);
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  
  const { register, handleSubmit, setError, formState: { errors }, setValue } = useForm({
    defaultValues: {
      name: formData.name || '',
      email: formData.email || '',
      password: formData.password || ''
    }
  });
  const navigate = useNavigate();
  const [companyType, setCompanyType] = useState(formData.company_type || 'brand');

  const userAlreadyRegistered = !!formData.user_id;

  useEffect(() => {
    if (formData.name) setValue('name', formData.name);
    if (formData.email) setValue('email', formData.email);
    if (formData.password) setValue('password', formData.password);
    if (formData.company_type) setCompanyType(formData.company_type);
  }, [formData, setValue]);
  
  const handleRegisterSubmit = async (data) => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      localStorage.setItem('company_type', companyType);
      dispatch(updateFormData({
        name: data.name,
        email: data.email,
        password: data.password,
        company_type: companyType
      }));

      let registrationResponse: any;
      let userWasCreated = false;
      
      try {
        registrationResponse = await new Promise((resolve, reject) => {
          api.registerUser(
            {
              name: data.name,
              email: data.email,
              password: data.password,
              company_type: companyType,
            }, 
            (res: any) => {
              if (res.data && res.data.id) {
                resolve(res);
              } else {
                reject(new Error('Registration failed - no user data returned'));
              }
            },
            (error: any) => {
              reject(error);
            }
          );
        });
        
        userWasCreated = true;
      } catch (registrationError: any) {
        try {
          const loginAttempt: any = await new Promise((resolve, reject) => {
            api.auth(
              {
                username: data.email,
                password: data.password,
                grant_type: "password",
                client_id: process.env.REACT_APP_CLIENT_ID,
                client_secret: process.env.REACT_APP_CLIENT_SECRET,
                scope: ""
              },
              (authRes: any) => {
                if (authRes && authRes.status === 200 && authRes.data.access_token) {
                  resolve(authRes);
                } else {
                  reject(new Error('Login verification failed'));
                }
              }
            );
          });
          
          userWasCreated = true;
          localStorage.setItem('access_token', loginAttempt.data.access_token);
          
          const userResponse: any = await new Promise((resolve, reject) => {
            api.getUser((userRes: any) => {
              if (userRes && userRes.data) {
                resolve(userRes);
              } else {
                reject(new Error('Failed to fetch user data'));
              }
            });
          });
          
          registrationResponse = { data: userResponse.data };
          
        } catch (loginError) {
          throw registrationError;
        }
      }
      
      dispatch(updateUser({ 
        id: registrationResponse.data.id, 
        name: registrationResponse.data.name, 
        email: registrationResponse.data.email 
      }));
      dispatch(updateFormData({ user_id: registrationResponse.data.id }));

      let authResponse: any;
      
      if (!localStorage.getItem('access_token')) {
        authResponse = await new Promise((resolve, reject) => {
          api.auth(
            {
              username: data.email,
              password: data.password,
              grant_type: "password",
              client_id: process.env.REACT_APP_CLIENT_ID,
              client_secret: process.env.REACT_APP_CLIENT_SECRET,
              scope: ""
            },
            (authRes: any) => {
              if (authRes && authRes.status === 200 && authRes.data.access_token) {
                resolve(authRes);
              } else {
                reject(new Error('Auto-login failed'));
              }
            }
          );
        });

        localStorage.setItem('access_token', authResponse.data.access_token);
      }

      let userResponse: any;
      
      if (registrationResponse.data.id && registrationResponse.data.name) {
        userResponse = { data: registrationResponse.data };
      } else {
        userResponse = await new Promise((resolve, reject) => {
          api.getUser((userRes: any) => {
            if (userRes && userRes.data) {
              resolve(userRes);
            } else {
              reject(new Error('Failed to fetch complete user data'));
            }
          });
        });
      }

      dispatch(updateUser(userResponse.data));

      await new Promise(resolve => setTimeout(resolve, 300));

      const userId = userResponse.data.id;
      
      api.getCompanyType(userId, (companyTypeRes) => {
        setTimeout(() => {
          if (companyTypeRes && companyTypeRes.data) {
            const serverCompanyType = companyTypeRes.data.company_type;
            const entityId = companyTypeRes.data.entity_id;

            if (serverCompanyType) {
              localStorage.setItem('company_type', serverCompanyType);
              dispatch(updateFormData({ company_type: serverCompanyType }));
            }

            if (serverCompanyType && entityId === 0) {
              navigate(`/register/${serverCompanyType}`, { 
                replace: true,
                state: { 
                  user: userResponse.data,
                  preserveFormData: true
                }
              });
            } else if (!serverCompanyType) {
              navigate('/register', { replace: true });
            } else {
              navigate('/', { replace: true });
            }
          } else {
            navigate(`/register/${companyType}`, { 
              replace: true,
              state: { 
                user: userResponse.data,
                preserveFormData: true
              }
            });
          }
          setIsSubmitting(false);
        }, 300);
      }, (error) => {
        setTimeout(() => {
          navigate(`/register/${companyType}`, { 
            replace: true,
            state: { 
              user: userResponse.data,
              preserveFormData: true
            }
          });
          setIsSubmitting(false);
        }, 300);
      });

    } catch (error: any) {
      setIsSubmitting(false);
      
      if (error.status === 422 && error.response?.data?.errors) {
        const errors = error.response.data.errors;
        
        if (errors.email && errors.email.length > 0) {
          const emailError = errors.email[0];
          if (emailError.includes('already') || emailError.includes('exists')) {
            setError("email", {
              type: "manual",
              message: "Account already exists. Please login instead.",
            });
          } else {
            setError("email", {
              type: "manual",
              message: emailError,
            });
          }
        }
        
        if (errors.name && errors.name.length > 0) {
          setError("name", {
            type: "manual",
            message: errors.name[0],
          });
        }
        
        if (errors.password && errors.password.length > 0) {
          setError("password", {
            type: "manual",
            message: errors.password[0],
          });
        }
      } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        setError("email", {
          type: "manual",
          message: "Network error occurred. If you don't receive a confirmation, please try logging in instead.",
        });
      } else if (error.status === 500) {
        setError("email", {
          type: "manual",
          message: "Server error occurred. If you don't receive a confirmation, please try logging in instead.",
        });
      } else {
        const errorMessage = error.response?.data?.message || 
                           error.message || 
                           "Registration failed. Please try again or login if account was created.";
        setError("email", {
          type: "manual",
          message: errorMessage,
        });
      }
    }
  };
  
  return (
    <div className={`nc-PageSignUp  ${className}`} data-nc-id="PageSignUp">
      <div className="flex flex-col md:flex-row w-full min-h-screen">
        {/* Left Side - Content */}
        <div className="w-full md:w-1/2 p-6 md:p-12 flex flex-col justify-start bg-white dark:bg-gray-900">
          <div className="max-w-2xl mx-auto pt-12 md:pt-16 animate-in fade-in slide-in-from-left-4 duration-700">
            <div className="space-y-8">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-white">
                  Welcome to AgentMatch
                </h1>
                <h2 className="text-xl md:text-2xl mt-4 text-gray-600 dark:text-gray-300">
                  Where Fashion Brands and Agents Connect
                </h2>
              </div>

              <div className="text-base md:text-lg space-y-4 text-gray-600 dark:text-gray-300 text-justify">
                <p>
                  AgentMatch is the go-to platform for fashion professionals ready to grow. We
                  connect <strong>ambitious brands</strong> looking to expand their reach with <strong>experienced agents</strong> searching for fresh, exciting labels to represent.
                </p>

                <p>
                  Whether you're a brand eager to break into new markets or an agent looking to
                  enrich your portfolio, AgentMatch brings the right people together. Our curated
                  network helps you build strong, strategic partnerships—because when the right
                  match is made, business takes off.
                </p>

                <p>
                  <strong>Join AgentMatch today and step into a world of new opportunities in fashion.</strong>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Contnt */}
        <div className="w-full md:w-1/2 p-6 md:p-12 bg-white dark:bg-gray-900 flex flex-col justify-start">
          <div className="max-w-md mx-auto w-full pt-12 md:pt-16 space-y-8 animate-in fade-in slide-in-from-right-4 duration-700">
            <div className="text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                Create Your Account
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Join the AgentMatch community
              </p>
            </div>

            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <Link
                to="/login"
                className="flex-1 py-2 px-4 text-center text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-md transition-all duration-200"
              >
                Login
              </Link>
              <button className="flex-1 py-2 px-4 text-center text-sm font-medium text-white bg-gray-900 dark:bg-white dark:text-black rounded-md transition-all duration-200">
                Register
              </button>
            </div>

            {/* Company Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                I am a:
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setCompanyType('brand')}
                  disabled={isSubmitting}
                  className={`p-3 text-sm font-medium rounded-lg border-2 transition-all duration-200 ${companyType === 'brand'
                      ? 'border-black dark:border-white bg-gray-900 dark:bg-white text-white dark:text-black'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-500 dark:hover:border-gray-400'
                    }`}
                >
                  Fashion Brand
                </button>

                <button
                  type="button"
                  onClick={() => setCompanyType('agency')}
                  disabled={isSubmitting}
                  className={`p-3 text-sm font-medium rounded-lg border-2 transition-all duration-200 ${companyType === 'agency'
                      ? 'border-black dark:border-white bg-gray-900 dark:bg-white text-white dark:text-black'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-500 dark:hover:border-gray-400'
                    }`}
                >
                  Fashion Agent
                </button>
              </div>
            </div>

            {/* FORM */}
            <form onSubmit={handleSubmit(handleRegisterSubmit)} className="space-y-6">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Your Name
                  </label>
                  <Input
                    type="text"
                    name='name'
                    placeholder="John Doe"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent dark:bg-gray-800 dark:text-white"
                    disabled={isSubmitting}
                    {...register('name', { required: { value: true, message: "Name field is required" } })}
                  />
                  {errors.name &&
                    <span className="mt-2 text-xs text-red-500">
                      {errors.name.message.toString()}
                    </span>
                  }
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email address
                  </label>
                  <Input
                    type="text"
                    name='email'
                    placeholder="example@example.com"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent dark:bg-gray-800 dark:text-white"
                    disabled={isSubmitting}
                    {...register("email", { 
                      required: {
                        value: true,
                        message: "Email field is required"
                      }, 
                      pattern: {
                        value: /\S+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "Please enter a valid email address"
                      } 
                    })}
                  />
                  {errors.email && 
                    <span className="mt-2 text-xs text-red-500">
                      {errors.email.message.toString()}
                    </span>
                  }
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Input 
                      type={showPassword ? "text" : "password"} 
                      name='password' 
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent dark:bg-gray-800 dark:text-white"
                      disabled={isSubmitting}
                      {...register('password', {
                        required: { value: true, message: "Password field is required" },
                        minLength: { value: 8, message: "Password minimum length is 8" }
                      })}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 focus:outline-none text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isSubmitting}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.password &&
                    <span className="mt-2 text-xs text-red-500">
                      {errors.password.message.toString()}
                    </span>
                  }
                </div>

                <ButtonPrimary
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 dark:text-gray-900 text-white font-medium py-3 px-4 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-white dark:border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                      <span>Creating Account...</span>
                    </div>
                  ) : (
                    "Create Account"
                  )}
                </ButtonPrimary>
              </div>
            </form>

          {/* ==== */}
          <div className="text-center">
            <span className="text-gray-600 dark:text-gray-400">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-black hover:text-gray-700 dark:text-white dark:hover:text-gray-300 font-medium transition-colors"
              >
                Sign in
              </Link>
            </span>
          </div>

          {/* Terms */}
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              By creating an account, you agree to our{" "}
              <Link to="/register" className="text-black hover:text-gray-700 dark:text-white dark:hover:text-gray-300">
                Terms of Service
              </Link>{" "}
                and{" "}
              <Link to="/register" className="text-black hover:text-gray-700 dark:text-white dark:hover:text-gray-300">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default PageSignUp;